/**
 * Ephemeris service: dynamic positions of the Sun, Moon, and planets.
 *
 * All positions are computed on demand from astronomy-engine's high-precision
 * theories (VSOP87-class planetary theory, ELP-style lunar theory), NOT from
 * a fixed catalog. Positions are therefore valid for any date.
 *
 * Magnitudes and angular diameters use astronomy-engine's Illumination and
 * angular-size models, which are accurate enough for sky-map display
 * (planetary magnitudes to ~0.1 mag).
 */

import {
  Body,
  GeoMoon,
  HelioDistance,
  Illumination,
  KM_PER_AU,
  MoonPhase,
  type IlluminationInfo,
} from "astronomy-engine";

import { skyEngine } from "./engine";
import type { BodyPosition, CelestialCoordinate, ObserverLocation, ObserverTime, SolarSystemBody } from "./types";
import { Planet } from "./types";

/**
 * Mean radii in km (IAU 2015 values, public reference data).
 * Used to compute apparent angular diameters from geocentric distance.
 */
const BODY_RADIUS_KM: Readonly<Record<SolarSystemBody, number>> = {
  Sun: 696_000,
  Moon: 1737.4,
  Mercury: 2439.7,
  Venus: 6051.8,
  Mars: 3389.5,
  Jupiter: 69_911,
  Saturn: 58_232,
  Uranus: 25_362,
  Neptune: 24_622,
};

const RAD2ARCSEC = (180 / Math.PI) * 3600;

function angularDiameterArcseconds(body: SolarSystemBody, distanceAu: number): number {
  const radiusKm = BODY_RADIUS_KM[body];
  // Angular radius as seen by the observer: asin(R / d); full diameter = 2×.
  return 2 * Math.asin(Math.min(1, radiusKm / (distanceAu * KM_PER_AU))) * RAD2ARCSEC;
}

function toAeBody(body: SolarSystemBody): Body {
  switch (body) {
    case "Sun":
      return Body.Sun;
    case "Moon":
      return Body.Moon;
    case Planet.Mercury:
      return Body.Mercury;
    case Planet.Venus:
      return Body.Venus;
    case Planet.Mars:
      return Body.Mars;
    case Planet.Jupiter:
      return Body.Jupiter;
    case Planet.Saturn:
      return Body.Saturn;
    case Planet.Uranus:
      return Body.Uranus;
    case Planet.Neptune:
      return Body.Neptune;
  }
}

export interface BodyEphemeris extends BodyPosition {
  /** Illumination details (phase angle, illuminated fraction). */
  illumination: {
    /** Fraction of the body's disk illuminated, [0, 1]. */
    fraction: number;
    /** Sun-object-observer phase angle in degrees. */
    phaseAngleDegrees: number;
  };
  /** Topocentric true-of-date coordinate. */
  coordinate: CelestialCoordinate;
}

/**
 * Full ephemeris for a solar-system body at a given observer/time.
 */
export function bodyEphemeris(body: SolarSystemBody, observer: ObserverLocation, time: ObserverTime): BodyEphemeris {
  const coordinate = skyEngine.bodyCoordinate(body, observer, time);
  const illum: IlluminationInfo = Illumination(toAeBody(body), new Date(time.utcMilliseconds));
  // GeoVector/GeoMoon return distances in AU already. The Sun's heliocentric
  // distance is ~0 by definition; its geocentric distance is the Earth-Sun
  // distance (~1 AU), i.e. the Earth's heliocentric distance.
  const distanceAu =
    body === "Moon"
      ? moonDistanceAu(time)
      : body === "Sun"
        ? HelioDistance(Body.Earth, new Date(time.utcMilliseconds))
        : illum.geo_dist;
  return {
    coordinate,
    distanceAu,
    magnitude: illum.mag,
    angularDiameterArcseconds: angularDiameterArcseconds(body, distanceAu),
    precision: "high",
    illumination: {
      fraction: illum.phase_fraction,
      phaseAngleDegrees: illum.phase_angle,
    },
  };
}

function moonDistanceAu(time: ObserverTime): number {
  // GeoMoon returns the geocentric position in AU.
  const v = GeoMoon(new Date(time.utcMilliseconds));
  return Math.hypot(v.x, v.y, v.z);
}

/** Convenience: all seven planets, in order of distance from the Sun. */
export const PLANETS: readonly Planet[] = [
  Planet.Mercury,
  Planet.Venus,
  Planet.Mars,
  Planet.Jupiter,
  Planet.Saturn,
  Planet.Uranus,
  Planet.Neptune,
];

/** Convenience: the naked-eye planets. */
export const NAKED_EYE_PLANETS: readonly Planet[] = [
  Planet.Mercury,
  Planet.Venus,
  Planet.Mars,
  Planet.Jupiter,
  Planet.Saturn,
];

/** Days since the last new moon (phase age). */
export function moonAgeDays(time: ObserverTime): number {
  // MoonPhase returns the phase angle in degrees; convert to days of a
  // mean synodic month (29.530588853 days).
  return (MoonPhase(new Date(time.utcMilliseconds)) / 360) * 29.530588853;
}
